import * as React from "react";

import { Resources, useResource } from "Translations/Resources";
import { TicketListDto, TicketSeverity, TicketType } from "Api/Api";

import { CancelIcon, WarningFilled } from "Components/Shared/Icons";
import { Colors } from "Components/Models/Colors";
import { DefaultButtonWithTooltip } from "Components/Shared/Buttons/DefaultButtonWithTooltip";
import { InfoIcon } from "Components/Shared/Icons/InfoIcon";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { PrimaryLink } from "Components/Shared/Routes/PrimaryLink";
import { PrimaryTooltip } from "Components/Shared/PrimaryTooltip";
import { TicketItemIcon } from "Components/Dashboard/TicketListItem/TicketItemIcon";
import { Typography } from "@material-ui/core";
import styled from "styled-components";
import {
  TicketsAfterDueDateIcon,
  TicketsCountIcon,
} from "Components/Dashboard/TicketListItem/TicketCountIcons";

const StyledWrapper = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: ${props => props.theme.spacing(0, 2)};
  padding: 8px 11px;
  border-bottom: 1px solid ${props => props.theme.colors.border};

  &:hover {
    background-color: ${props => props.theme.palette.secondary.light};
  }

  .title-column {
    min-width: 0;
  }
  .title-row {
    display: flex;
  }
  .button-column {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    padding-bottom: 5px;
    position: relative;

    .action-buttons {
      flex-wrap: wrap;
      justify-content: flex-end;

      .resolve-btn {
        order: 2;
        justify-content: flex-end;
      }

      .cancel-btn {
        position: absolute;
        top: 0;
        right: 0;

        button {
          padding: 0;
        }
      }
    }
  }
  ${props => props.theme.breakpoints.down("xs")} {
    position: relative;

    .title-column {
      grid-area: 1/2/1/3;
    }

    .button-column {
      grid-area: 2/2/2/3;
      position: static;

      .action-buttons {
        margin-right: -20px;

        .cancel-btn {
          top: 5px;
        }
      }
    }
  }
`;

const StyledIcon = styled.div``;

const StyledActionButtons = styled.div`
  display: flex;
  align-items: center;
  & > div {
    display: flex;
  }
  .info {
    margin-left: 10px;
    width: 16px;
  }
  svg {
    height: auto;
  }
  svg:not(.right-arrow) {
    width: 20px;
  }
  svg.right-arrow {
    width: 21.5px;
  }

  .additional-info {
    margin-left: ${props => props.theme.spacing(1)}px;
    svg {
      width: 18px;
    }
  }
`;

const StyledTitle = styled.div<{ color: string }>`
  min-width: 1px;

  p {
    color: ${props => props.color};
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    max-width: 100%;
  }
`;

const StyledDescription = styled(Typography)`
  font-weight: 300;
`;

const StyledFixUntil = styled.div`
  display: flex;
  color: ${props => props.theme.palette.secondary.main};
  p {
    &:first-of-type {
      margin-right: 5px;
      font-weight: 300;
    }
    &:last-of-type {
      text-transform: uppercase;
    }
  }
`;

const StyledPrimaryButton = styled(PrimaryButton)`
  width: 90px;
  padding: ${props => props.theme.spacing(0.125, 1)};
  font-size: ${props => props.theme.typography.h5.fontSize}px;
`;

const StyledExternalLink = styled.a`
  cursor: pointer;
  text-decoration: none;
`;

export type TicketItemProps = TicketListDto & {
  isProcessing?: boolean;
  isDisabled?: boolean;
};

type TicketListItemProps = {
  item: TicketItemProps;
  onResolveClick: (ticketID: number, groupCode?: string | null) => void;
  onCancelClick: (ticketID: number, groupCode?: string | null) => void;
  hideActionIcons?: boolean;
};

const TicketListItem: React.FunctionComponent<TicketListItemProps> = props => {
  const { t } = useResource();
  const { item } = props;

  const modifyDescription = (description: string) => {
    return (
      <span
        dangerouslySetInnerHTML={{
          __html: description.replace(" %", "&nbsp;%"),
        }}
      ></span>
    );
  };

  const dueDateIconsCss: React.CSSProperties = {
    display: "inline",
    fontSize: "13.5px",
    height: "18px",
    minWidth: "18px",
    padding: "0 5px",
    paddingLeft: "6px",
    marginLeft: "10px",
    lineHeight: "17px",
    marginTop: "2px",
    marginRight: "3px",
  };

  return (
    <StyledWrapper>
      <StyledIcon>
        <TicketItemIcon ticket={item} iconColor={getColor(item.severity)} />
      </StyledIcon>
      <div className={"title-column"}>
        <div className={"title-row"}>
          <PrimaryTooltip title={item.title} placement="top-start">
            <StyledTitle color={getColor(item.severity)}>
              <Typography>{item.title} </Typography>
            </StyledTitle>
          </PrimaryTooltip>
          {item.ticketsInGroupCount !== 0 && (
            <>
              {item.ticketsAfterDueDateCount !== 0 && (
                <TicketsAfterDueDateIcon
                  styles={dueDateIconsCss}
                  count={item.ticketsAfterDueDateCount}
                />
              )}

              <TicketsCountIcon count={item.ticketsInGroupCount} />
            </>
          )}
          <StyledActionButtons>
            {item.tooltip && (
              <PrimaryTooltip
                title={item.tooltip}
                placement="top-start"
                className={"info"}
              >
                <div>
                  <InfoIcon />
                </div>
              </PrimaryTooltip>
            )}
          </StyledActionButtons>

          {!!item.additionalInfo && (
            <StyledActionButtons>
              <PrimaryTooltip
                title={item.additionalInfo}
                className={"additional-info"}
                placement={"top-start"}
              >
                <div>
                  <WarningFilled />
                </div>
              </PrimaryTooltip>
            </StyledActionButtons>
          )}
        </div>

        <StyledDescription>
          {modifyDescription(item.description)}
        </StyledDescription>
        {item.dueDate &&
          isEmptyString(item.groupCode) &&
          getDueDateText(new Date(item.dueDate), t)}
        {item.externalLinkUrl && (
          <div>
            <PrimaryLink
              link={item.externalLinkUrl}
              text={item.externalLinkText}
            />
          </div>
        )}
      </div>
      <div className={"button-column"}>
        {!props.hideActionIcons && (
          <StyledActionButtons className={"action-buttons"}>
            {item.type !== TicketType.Reminder &&
              getActionIcons(props, item, t)}
          </StyledActionButtons>
        )}
      </div>
    </StyledWrapper>
  );
};

const isEmptyString = (str: string | undefined | null) => {
  return str === undefined || str === null || str === "";
};

const getColor = (severity: TicketSeverity) => {
  switch (severity) {
    case TicketSeverity.Warning:
      return Colors.Yellow;
    case TicketSeverity.Critical:
      return Colors.Red;
    default:
      return Colors.Text;
  }
};

const getDueDateText = (
  dueDate: Date,
  t: (resourcePath: string, options?: object | undefined) => string,
) => {
  let color = getColor(TicketSeverity.Info),
    text = "";

  let now = new Date();
  let today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let dayDiff = (dueDate.getTime() - today.getTime()) / (24 * 3600 * 1000);
  let ceiledAbsDiff = Math.ceil(Math.abs(dayDiff));

  if (dayDiff >= 0 && dayDiff < 1) {
    text = t(Resources.Dashboard.Tickets.FixUntil.Today);
    color = getColor(TicketSeverity.Critical);
  } else if (dayDiff > 1 && dayDiff < 2) {
    text = t(Resources.Dashboard.Tickets.FixUntil.Tomorrow);
    color = getColor(TicketSeverity.Warning);
  } else if (dayDiff < 0 && dayDiff >= -1) {
    text = t(Resources.Dashboard.Tickets.FixUntil.Yesterday);
    color = getColor(TicketSeverity.Critical);
  } else if (dayDiff < 0) {
    text = t(Resources.Dashboard.Tickets.FixUntil.BeforeDays, {
      days: ceiledAbsDiff,
    });
    color = getColor(TicketSeverity.Critical);
  } else if (dayDiff > 1 && dayDiff < 5) {
    text = t(Resources.Dashboard.Tickets.FixUntil.AfterFewDays, {
      days: ceiledAbsDiff - 1,
    });
  } else {
    text = t(Resources.Dashboard.Tickets.FixUntil.AfterManyDays, {
      days: ceiledAbsDiff - 1,
    });
  }

  return (
    <StyledFixUntil>
      <Typography>{t(Resources.Dashboard.Tickets.FixUntil.Label)}</Typography>
      <Typography
        style={{
          color: color,
        }}
      >
        {text}
      </Typography>
    </StyledFixUntil>
  );
};

const getActionIcons = (
  props: TicketListItemProps,
  item: TicketItemProps,
  t: (resourcePath: string, options?: object | undefined) => string,
) => {
  const cancelBtn = item.isCancelationDisabled ? (
    <></>
  ) : (
    <DefaultButtonWithTooltip
      title={t(Resources.Dashboard.Tickets.Cancel)}
      onClick={_ => props.onCancelClick(item.ticketID, item.groupCode)}
      className={"cancel-btn"}
      placement="top-start"
    >
      <CancelIcon />
    </DefaultButtonWithTooltip>
  );

  const getText = () => {
    if (item.severity === TicketSeverity.Info) {
      return "Přečteno";
    }

    return item.externalLinkUrl
      ? t(Resources.Dashboard.Tickets.Confirm)
      : t(Resources.Dashboard.Tickets.Resolved);
  };

  return (
    <>
      {
      item.type === TicketType.CustomActions? (
        <>
        </>
      ) : 
      item.externalLinkUrl && item.severity !== TicketSeverity.Info ? (
        <>
          <StyledExternalLink
            href={item.externalLinkUrl}
            target="_blank"
            className={"resolve-btn"}
          >
            <StyledPrimaryButton
              onClick={_ => props.onResolveClick(item.ticketID, item.groupCode)}
            >
              {getText()}
            </StyledPrimaryButton>
          </StyledExternalLink>
          {cancelBtn}
        </>
      ) : (
        <StyledPrimaryButton onClick={_ => props.onResolveClick(item.ticketID)}>
          {getText()}
        </StyledPrimaryButton>
      )}
    </>
  );
};

export { TicketListItem };
